/**
 * userType.js
 *
 * Helper to determine effective user type, with backward compatibility
 * for users not yet migrated to the new userType field.
 */

/**
 * Get the effective user type from a user object.
 * Supports both new `userType` field and legacy `role` field.
 *
 * @param {object} user - The user object from auth context
 * @returns {"internal"|"client"|"partner"|"processor"|null}
 */
export function getUserType(user) {
  if (!user) return null;
  // `processor` (SOUND-559) is new — it only ever arrives via the modern
  // `userType` field, so this passthrough covers it; there is no legacy
  // `role` to infer it from below.
  if (user.userType) return user.userType;

  // Backward compat: infer from old role field
  switch (user.role) {
    case "admin":
      return "internal";
    case "customer":
      return "client";
    case "partner":
      return "partner";
    default:
      return null;
  }
}

/**
 * Check if user is an internal (Sound Commerce) user.
 * @param {object} user
 * @returns {boolean}
 */
export function isInternal(user) {
  return getUserType(user) === "internal";
}

/**
 * Check if user is a client (merchant) user.
 * @param {object} user
 * @returns {boolean}
 */
export function isClient(user) {
  return getUserType(user) === "client";
}

/**
 * Check if user is a partner user.
 * @param {object} user
 * @returns {boolean}
 */
export function isPartner(user) {
  return getUserType(user) === "partner";
}

/**
 * Check if user is a processor (payment processor / acquirer) user.
 * Processors (SOUND-559, Kurv first) get a scoped, read-only risk +
 * MID-monitoring surface — no CRM. The API is default-deny and MID-grained;
 * this helper only drives the UI (nav, routing, hiding write affordances).
 * @param {object} user
 * @returns {boolean}
 */
export function isProcessor(user) {
  return getUserType(user) === "processor";
}

/**
 * Current Partner Agent Terms version. Bump when placeholder text is replaced
 * (or any material change ships) so previously-accepted partners re-prompt.
 */
export const PARTNER_AGENT_TERMS_VERSION = 1;

/**
 * Returns true when the user is a partner who has not yet accepted the current
 * version of the Partner Agent Terms. Internal and client users never need to
 * accept.
 *
 * @param {object} user
 * @returns {boolean}
 */
export function needsPartnerAgentTerms(user) {
  if (!user) return false;
  if (!isPartner(user)) return false;
  if (user.acceptedPartnerAgentTerms !== true) return true;
  if (user.partnerAgentTermsVersion !== PARTNER_AGENT_TERMS_VERSION) return true;
  return false;
}
